/**
 * The Family Builder's pure half: parameters from a view, the build evidence on
 * the stream, the activity wording for the family agent's tools, and v2's
 * refresh guards. No React, no network — `tests/family-builder.test.mjs` runs it
 * as is.
 */
import type { FamilyBuildEvidence, FamilyParameter, FamilyView } from "./types";

/** The family agent's thread title — the agent creates the thread under it. */
export const FAMILY_THREAD_TITLE = "Revit Family Builder";

/** Every title a family thread has carried in 2.x, current first (v2 `familyAuthoringConstants.ts`). */
const FAMILY_THREAD_TITLES: ReadonlySet<string> = new Set([
  FAMILY_THREAD_TITLE,
  "Revit Component Builder",
  "Revit Family Authoring",
]);

/**
 * Whether a conversation belongs to the Family Builder. Such a thread is the
 * family agent's: it stays out of the chat history, as in v2, because opening
 * it there would continue it with the main agent instead.
 */
export function isFamilyBuilderThread(title: string | null | undefined): boolean {
  return FAMILY_THREAD_TITLES.has(title?.trim() ?? "");
}

// --- parameters ----------------------------------------------------------------

function isParameterValue(value: unknown, type: FamilyParameter["type"]): value is FamilyParameter["value"] {
  return typeof value === type;
}

/**
 * The controls a view offers: its schema, valued from the last run (else the
 * schema's default, else a neutral value). Only when the contract is valid — an
 * invalid one shows the repair prompt instead. v2's `parametersFromView`.
 */
export function parametersFromView(view: FamilyView): FamilyParameter[] {
  if (view.parameterContract?.status !== "valid") return [];
  const values = view.artifact.lastUsedParameters ?? {};
  return (view.artifact.parameterSchema ?? []).flatMap((parameter) => {
    if (!parameter.name || !["number", "integer", "string", "boolean"].includes(parameter.type ?? "")) return [];
    const raw = Object.prototype.hasOwnProperty.call(values, parameter.name) ? values[parameter.name] : parameter.default;
    const type = (parameter.type === "integer" ? "number" : parameter.type) as FamilyParameter["type"];
    const fallback = type === "number" ? 0 : type === "boolean" ? false : "";
    return [
      {
        key: parameter.name,
        label: typeof parameter.label === "string" ? parameter.label : parameter.name,
        type,
        value: isParameterValue(raw, type) ? raw : fallback,
        ...(typeof parameter.unit === "string" ? { unit: parameter.unit } : {}),
        ...(typeof parameter.minimum === "number" ? { minimum: parameter.minimum } : {}),
        ...(typeof parameter.maximum === "number" ? { maximum: parameter.maximum } : {}),
        ...(typeof parameter.step === "number" ? { step: parameter.step } : {}),
      },
    ];
  });
}

/** Whether the controls differ from what the view last applied. */
export function hasParameterChanges(view: FamilyView | null, parameters: readonly FamilyParameter[]): boolean {
  if (!view || view.parameterContract?.status !== "valid") return false;
  const applied = parametersFromView(view);
  return parameters.some((parameter, index) => parameter.value !== applied[index]?.value);
}

/** The values `execute` sends. */
export function parameterValues(parameters: readonly FamilyParameter[]): Record<string, unknown> {
  return Object.fromEntries(parameters.map((parameter) => [parameter.key, parameter.value]));
}

// --- build evidence --------------------------------------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
const num = (value: unknown): number | null => (typeof value === "number" && Number.isFinite(value) ? value : null);

/**
 * A `family_build_evidence` event → the evidence, or null for any other event or
 * one too malformed to draw. The agent emits it right after `build_family`'s
 * `tool_end`, once per successful Build.
 */
export function readBuildEvidence(event: Record<string, unknown>): FamilyBuildEvidence | null {
  if (event["type"] !== "family_build_evidence") return null;
  const evidence = event["evidence"];
  if (!isRecord(evidence)) return null;
  const summary = isRecord(evidence["geometrySummary"]) ? evidence["geometrySummary"] : null;
  const box = summary && isRecord(summary["axisAlignedBoundingBoxMm"]) ? summary["axisAlignedBoundingBoxMm"] : null;
  const shot = isRecord(evidence["screenshot"]) ? evidence["screenshot"] : null;
  const version = num(evidence["version"]);
  if (!summary || !box || !shot || version === null) return null;
  const imageUrl = typeof shot["imageUrl"] === "string" ? shot["imageUrl"] : "";
  if (!imageUrl) return null;
  const status = evidence["status"] === "success" || evidence["status"] === "partial_success" ? evidence["status"] : undefined;
  return {
    ...(status ? { status } : {}),
    version,
    ...(typeof evidence["message"] === "string" ? { message: evidence["message"] } : {}),
    geometrySummary: {
      freeFormElementCount: num(summary["freeFormElementCount"]) ?? 0,
      axisAlignedBoundingBoxMm: {
        xExtent: num(box["xExtent"]) ?? 0,
        yExtent: num(box["yExtent"]) ?? 0,
        zExtent: num(box["zExtent"]) ?? 0,
      },
    },
    screenshot: {
      imageUrl,
      viewName: typeof shot["viewName"] === "string" ? shot["viewName"] : "",
      width: num(shot["width"]) ?? 0,
      height: num(shot["height"]) ?? 0,
    },
  };
}

/** "1200 × 600 × 750" — the bounding box line under a screenshot. */
export function evidenceDimensions(evidence: FamilyBuildEvidence): string {
  const box = evidence.geometrySummary.axisAlignedBoundingBoxMm;
  return [box.xExtent, box.yExtent, box.zExtent].map((value) => Number(value.toFixed(1))).join(" × ");
}

// --- activity ------------------------------------------------------------------

/**
 * What each of the family agent's tools is doing, in the user's words — v2's
 * `FamilyAuthoringActivity` groups. The desktop's step rows show the label in
 * place of the tool name, so a family turn reads as "Building and visually
 * validating the family" rather than `build_family`.
 */
const ACTIVITY_LABELS: ReadonlyArray<readonly [string, string]> = [
  ["read_file", "familyBuilder.activity.reviewingArtifacts"],
  ["grep", "familyBuilder.activity.reviewingArtifacts"],
  ["edit_file", "familyBuilder.activity.updatingArtifacts"],
  ["edit_and_validate_family_code", "familyBuilder.activity.editingCode"],
  ["inspect_revit_with_code", "familyBuilder.activity.inspecting"],
  ["build_family", "familyBuilder.activity.building"],
];

export function activityLabelKey(tool: string): string {
  const match = ACTIVITY_LABELS.find(([name]) => tool === name || tool.endsWith(`__${name}`));
  return match ? match[1] : "familyBuilder.activity.generic";
}

// --- refresh guards (v2's `familyAuthoringViewRefresh.ts`) --------------------------

/** Load a view and apply it only if the request is still the current one. */
export async function applyCurrentView<T>(
  load: () => Promise<T>,
  isCurrent: () => boolean,
  apply: (view: T) => void,
): Promise<boolean> {
  const view = await load();
  if (!isCurrent()) return false;
  apply(view);
  return true;
}

/** After a successful turn: idle when the editor caught up with disk, error when it could not. */
export function successfulTurnRefreshStatus(
  turnSucceeded: boolean,
  result: PromiseSettledResult<boolean>,
): "idle" | "error" | null {
  if (!turnSucceeded) return null;
  return result.status === "fulfilled" && result.value ? "idle" : "error";
}
